import type { DeepReadonly, Ref } from 'vue'
import type { ContextMenu } from '@/composables/useContextMenu'

interface Params {
  contextMenu: DeepReadonly<Ref<ContextMenu>>
  onClose: () => void
  domElement: Ref<HTMLElement | null>
}

export interface UseContextMenuOverlay {
  overlayPointerEventsEnabled: Readonly<Ref<boolean>>
}

export function useContextMenuOverlay({ contextMenu, domElement, onClose }: Params): UseContextMenuOverlay {
  const navigationKeys = Object.freeze({
    TAB: 9,
    SPACE_BAR: 32,
    PAGE_UP: 33,
    PAGE_DOWN: 34,
    END: 35,
    HOME: 36,
    LEFT: 37,
    UP: 38,
    RIGHT: 39,
    DOWN: 40,
  })
  const LEFT_CLICK_EVENT = 0
  const RIGHT_CLICK_EVENT = 2
  const clickEvents = ['mousedown', 'touchstart']
  const overlayPointerEventsEnabled = ref(false)
  const keydownEvents = useKeyboardDisabler(Object.values(navigationKeys))

  watch(
    [() => contextMenu.value.show, () => contextMenu.value.origin, domElement],
    ([show, _origin, domElement]) => {
      if (!domElement) return

      if (show) {
        domElement.addEventListener('mouseup', _closeOverlay)
        clickEvents.forEach(evt => domElement.addEventListener(evt, _disablePointerEvents))
        keydownEvents.register()
        overlayPointerEventsEnabled.value = true
      } else {
        domElement.removeEventListener('mouseup', _closeOverlay)
        clickEvents.forEach(evt => domElement.removeEventListener(evt, _disablePointerEvents))
        keydownEvents.unregister()
      }
    },
    { immediate: true }
  )

  function _closeOverlay(e: any) {
    if (e.button === LEFT_CLICK_EVENT) {
      onClose()
    }
  }

  function _disablePointerEvents(e: any) {
    if (e.button !== RIGHT_CLICK_EVENT) return

    if (overlayPointerEventsEnabled.value && contextMenu.value.show) {
      onClose()
    } else {
      overlayPointerEventsEnabled.value = false
    }
  }

  return { overlayPointerEventsEnabled: readonly(overlayPointerEventsEnabled) }
}

interface UseKeyboardDisabler {
  register(): void
  unregister(): void
}

function useKeyboardDisabler(navigationKeys: number[]): UseKeyboardDisabler {
  const keys = new Set(navigationKeys)

  function register() {
    document.addEventListener('keydown', _preventDefaultForKeys)
  }

  function unregister() {
    document.removeEventListener('keydown', _preventDefaultForKeys)
  }

  function _preventDefaultForKeys(e: KeyboardEvent) {
    if (keys.has(e.keyCode)) {
      e.preventDefault()
      return false
    }
  }

  return { register, unregister }
}
